import React, { useEffect, useState } from 'react';
import {
  Container,
  StyledImage,
  continueButtonStyle,
  goalCardRegisteredTrademarkStyle,
  leftCardStyle,
  mainCardsWrapperStyle,
  rightCardStyle,
  rowPaddingTop,
  termsLinkStyle,
  termsStyle,
  textStyle,
  titleStyle,
  StyledInput,
  StyledZipCodeInput,
  flexContainerStyle,
  serviceFeeflexContainerStyle,
  fullWidth,
} from './styles';
import {
  Button,
  Card,
  Colors,
  Text,
  TypesButton,
  Typography,
} from 'goalsetter-webapp-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { useMediaQueryGs } from 'app/hooks/useMediaQuery';
import { Flex } from '@mantine/core';
import { FamilyMember } from 'types/goals';
import { CardsEntity } from 'types/goalcard';
import { Trans } from 'react-i18next';
import { BASE_API, getFee } from 'utils';
import { INPUT_NAMES } from './inputNames';
import { useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';
import {
  CardNumberElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { goalcardService } from 'api';
import { sendAnalyticsEvent } from 'utils/analytics/analyticsWrapper';
import { CreateGoalCardPayload, GsErrorCode } from 'types/api';
import useFetchData from 'app/hooks/useFetchData';
import { AxiosError } from 'axios';
import { validationHandler } from './validationHandler';
import StripeErrorModal from 'app/components/Modals/StripeErrorModal';
import { RoutesGs } from 'router/RoutesList';
import StripeCardNumberInput from './StripeCardNumberInput';
import StripeCardExpiryInput from './StripeCardExpiryInput';
import StripeCardCvcInput from './StripeCardCvcInput';
import { ActionType, CategoryType } from '../../../../types/analytics/enum';
import LogoHeader from 'app/components/LogoHeader';

export const Purchase: React.FC = () => {
  const { state } = useLocation();
  const { isMobile } = useMediaQueryGs();
  const navigate = useNavigate();

  const user = state?.selectedUser as FamilyMember;
  const card = state?.selectedCard as CardsEntity;
  const customizeData = state?.customizeData;
  const imageUrl = `${BASE_API}/${card?.imageURL}`;

  const [cardHolder, setCardHolder] = useState<string>('');
  const [zipCode, setZipCode] = useState<string>('');
  const [cardNumberError, setCardNumberError] = useState<string>('');
  const [cardExpiryError, setCardExpiryError] = useState<string>('');
  const [cardCvcError, setCardCvcError] = useState<string>('');
  const [stripeError, setStripeError] = useState<number | undefined>();
  const [cardNumberComplete, setCardNumberComplete] = useState<boolean>(false);
  const [expiryDateComplete, setExpiryDateComplete] = useState<boolean>(false);
  const [cvcComplete, setCvcComplete] = useState<boolean>(false);

  const stripe = useStripe();
  const elements = useElements();

  const nonSpecificGoalAmount = customizeData?.goalCardAmount;
  const goalsTotal = customizeData?.goalsSupportTotal;
  const total = nonSpecificGoalAmount ? nonSpecificGoalAmount : goalsTotal;
  const fee = getFee(String(total), false);
  const totalWithFee = getFee(String(total), true);

  const goToSelectUser = () => navigate('/');
  const goToSelectCard = () => navigate(RoutesGs.SelectGoalcard);
  const goToSuccessScreen = () =>
    navigate(RoutesGs.SuccessGoalcard, {
      state: {
        ...state,
      },
    });
  const {
    register,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const {
    data: paymentResp,
    loading,
    fetchData,
    error,
  } = useFetchData<unknown, AxiosError>();

  useEffect(() => {
    const paymentError = error as unknown as {
      'status-code': number;
      'status-description': string;
    };
    if (paymentError && paymentError['status-code']) {
      setStripeError(paymentError['status-code']);
    }
  }, [error]);

  // Clear Form errors on a change
  useEffect(() => {
    if (!isEmpty(errors)) clearErrors();
  }, [cardHolder, zipCode, clearErrors]);

  const validateCardholder = () =>
    validationHandler(setError, cardHolder, INPUT_NAMES.CARD_HOLDER);
  const validateZipCode = () =>
    validationHandler(setError, zipCode, INPUT_NAMES.ZIP_CODE);
  const disableSubmitButton =
    !isEmpty(errors) ||
    !!paymentResp ||
    cardNumberError.length > 0 ||
    cardExpiryError.length > 0 ||
    cardCvcError.length > 0 ||
    !cardHolder ||
    !zipCode ||
    !cardNumberComplete ||
    !expiryDateComplete ||
    !cvcComplete;

  const closeModal = () => setStripeError(undefined);
  const handleSubmit = async () => {
    if (validateCardholder() || validateZipCode()) return;

    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) return;
    const data = { name: cardHolder, address_zip: zipCode };
    const { token, error: createTokenError } = await stripe.createToken(
      cardElement,
      data,
    );

    if (createTokenError) {
      setStripeError(GsErrorCode.CreditCardDeclined);
    }

    if (!token) return;
    sendAnalyticsEvent(
      CategoryType.submitPaymentButton,
      ActionType.Goalcard_GoalcardPayment_SubmitPaymentButton,
    );
    const destinationLastName = user.fullName.replace(user.firstname, '');
    const uploadedMediaContent = () => {
      if (customizeData.uploadedMedia) {
        return { base64Image: customizeData.uploadedMedia };
      }
      if (customizeData.videoUrl) {
        return { videoUrl: customizeData.videoUrl };
      }
      return {};
    };

    const createGoalCardPayloadChilds: Array<{
      key: string;
      value: string;
    }> = customizeData.childs
      ? customizeData.childs.filter((a: { key: string; value: string }) => {
          const numericValue = parseInt(a.value);
          return !isNaN(numericValue) && numericValue > 0;
        })
      : [];

    const createGoalCardPayload: CreateGoalCardPayload = {
      amount: Number(total),
      childs: customizeData.isSpecified ? createGoalCardPayloadChilds : [],
      creditCardToken: token?.id,
      description: customizeData.message || '',
      destinationFirstname: user.firstname,
      destinationId: user.rid,
      destinationLastname: destinationLastName,
      executionDate: customizeData.date || '',
      investorEmail: customizeData.email,
      investorName: customizeData.name,
      isSpecified: customizeData.isSpecified,
      supportType: customizeData.isSpecified ? 'specific' : 'nonspecific',
      valuesByGoal: customizeData.isSpecified ? customizeData.goalsSupport : [],
      imageUrl: customizeData.uploadedMedia || '',
      videoUrl: customizeData.videoUrl || '',
      uploadedMedia: uploadedMediaContent(),
      goalcardImage: customizeData.goalcardImage,
    };

    fetchData(goalcardService.createGoalcard(createGoalCardPayload));
  };

  useEffect(() => {
    if (paymentResp) {
      goToSuccessScreen();
    }
  }, [paymentResp]);

  useEffect(() => {
    if (!user) {
      goToSelectUser();
      return;
    }
    if (!card) {
      goToSelectCard();
      return;
    }
  }, [user, card]);

  const handleCardNumberChange = (event: {
    complete?: boolean;
    error:
      | { type: 'validation_error'; code: string; message: string }
      | undefined;
  }) => {
    setCardNumberError('');
    setCardNumberComplete(false);
    if (event.complete) {
      setCardNumberComplete(true);
    }
    if (event.error) {
      setCardNumberError(
        String(t('purchase.formInputs.formErrors.cardNumber.wrong')),
      );
    }
    return;
  };

  const handleCardNumberOnBlur = () => {
    if (!cardNumberComplete) {
      setCardNumberError(
        String(t('purchase.formInputs.formErrors.cardNumber.empty')),
      );
    }
  };

  const handleCardExpiryChange = (event: {
    complete?: boolean;
    error:
      | { type: 'validation_error'; code: string; message: string }
      | undefined;
  }) => {
    setCardExpiryError('');
    setExpiryDateComplete(false);
    if (event.complete) {
      setExpiryDateComplete(true);
    }
    if (event.error) {
      setCardExpiryError(
        String(t('purchase.formInputs.formErrors.mmyy.wrong')),
      );
    }
    return;
  };

  const handleCardExpiryOnBlur = () => {
    if (!expiryDateComplete) {
      setCardExpiryError(
        String(t('purchase.formInputs.formErrors.mmyy.empty')),
      );
    }
  };

  const handleCardCvcChange = (event: {
    complete?: boolean;
    error:
      | { type: 'validation_error'; code: string; message: string }
      | undefined;
  }) => {
    setCardCvcError('');
    setCvcComplete(false);
    if (event.complete) {
      setCvcComplete(true);
    }
    if (event.error) {
      setCardCvcError(String(t('purchase.formInputs.formErrors.cvc.wrong')));
    }
    return;
  };

  const handleCardCvcOnBlur = () => {
    if (!cvcComplete) {
      setCardCvcError(String(t('purchase.formInputs.formErrors.cvc.empty')));
    }
  };

  const handleCardHolderChange = (value: string) => {
    clearErrors(INPUT_NAMES.CARD_HOLDER);
    setCardHolder(value);
  };

  const handleZipCodeChange = (value: string) => {
    clearErrors(INPUT_NAMES.ZIP_CODE);
    setZipCode(value);
  };

  return (
    <Container>
      <StripeErrorModal
        show={!!stripeError}
        exception={stripeError}
        onClose={closeModal}
      />
      <LogoHeader />
      <Text
        type={isMobile ? Typography.GSH4 : Typography.GSH1}
        color={Colors.WHITE}
        style={titleStyle(isMobile)}
      >
        {t('purchase.title')}
      </Text>

      <Flex
        direction={isMobile ? 'column' : 'row'}
        style={mainCardsWrapperStyle}
      >
        <Card style={leftCardStyle(isMobile)}>
          <Text
            type={Typography.GSH6}
            color={Colors.BLUE_INVESTMENTS}
          >
            {t('purchase.orderPayment')}
          </Text>
          <StyledInput
            label={String(t('purchase.formInputs.cardHolder.label'))}
            placeholder={t('purchase.formInputs.cardHolder.placeholder')}
            value={cardHolder}
            onChangeValue={handleCardHolderChange}
            onBlur={validateCardholder}
            name={INPUT_NAMES.CARD_HOLDER}
            register={register}
            error={
              errors[INPUT_NAMES.CARD_HOLDER]?.message
                ? String(errors[INPUT_NAMES.CARD_HOLDER]?.message)
                : undefined
            }
          />
          <Text
            type={Typography.GSCAPTION}
            color={Colors.GRAY_DARK}
          >
            {t('purchase.formInputs.cardNumber.label')}
          </Text>
          <StripeCardNumberInput
            cardNumberError={cardNumberError}
            handleCardNumberChange={handleCardNumberChange}
            handleCardNumberOnBlur={handleCardNumberOnBlur}
          />
          <Text
            type={Typography.GSBODY3}
            color={Colors.RED}
          >
            {cardNumberError}
          </Text>
          <Flex
            align="flex-start"
            gap="sm"
            style={flexContainerStyle}
          >
            <div style={fullWidth}>
              <StripeCardExpiryInput
                cardExpiryError={cardExpiryError}
                handleCardExpiryChange={handleCardExpiryChange}
                handleCardExpiryOnBlur={handleCardExpiryOnBlur}
              />
              <Text
                type={Typography.GSBODY3}
                color={Colors.RED}
              >
                {cardExpiryError}
              </Text>
            </div>
            <div style={fullWidth}>
              <StripeCardCvcInput
                cardCvcError={cardCvcError}
                handleCardCvcChange={handleCardCvcChange}
                handleCardCvcOnBlur={handleCardCvcOnBlur}
              />
              <Text
                type={Typography.GSBODY3}
                color={Colors.RED}
              >
                {cardCvcError}
              </Text>
            </div>
            <StyledZipCodeInput
              placeholder={t('purchase.formInputs.zipCode.placeholder')}
              value={zipCode}
              onChangeValue={handleZipCodeChange}
              maxLength={5}
              onBlur={validateZipCode}
              name={INPUT_NAMES.ZIP_CODE}
              register={register}
              error={
                errors[INPUT_NAMES.ZIP_CODE]?.message
                  ? String(errors[INPUT_NAMES.ZIP_CODE]?.message)
                  : undefined
              }
            />
          </Flex>
          {!isMobile && (
            <div style={termsStyle(false)}>
              <Button
                type={TypesButton.PRIMARY}
                onClick={handleSubmit}
                style={continueButtonStyle}
                isDisabled={disableSubmitButton}
                isLoading={loading}
              >
                {t('purchase.submitPayment')}
              </Button>
              <Trans
                i18nKey={t('purchase.terms')}
                components={{
                  span: (
                    <Link
                      to="https://stripe.com/legal/end-users"
                      target="_blank"
                      style={termsLinkStyle}
                      data-testid="linkStripeTerms"
                    />
                  ),
                }}
              />
            </div>
          )}
        </Card>

        <Card style={rightCardStyle(isMobile)}>
          <Text
            type={Typography.GSH6}
            color={Colors.BLUE_INVESTMENTS}
          >
            {t('purchase.orderSummary')}
          </Text>
          {card && (
            <StyledImage
              src={imageUrl}
              withPlaceholder
            />
          )}
          <Flex
            align="center"
            justify="space-between"
            style={rowPaddingTop}
          >
            <Text type={Typography.GSCAPTION}>
              <Trans
                i18nKey={`<span>To</span> ${user?.firstname || '-'}`}
                components={{
                  span: <span style={textStyle} />,
                }}
              />
            </Text>
            <Text type={Typography.GSCAPTION}>
              <Trans
                i18nKey={`<span>From</span> ${customizeData?.name || '-'}`}
                components={{
                  span: <span style={textStyle} />,
                }}
              />
            </Text>
          </Flex>

          <Flex
            align="center"
            justify="space-between"
            style={rowPaddingTop}
          >
            <Text
              type={Typography.GSBODY3}
              color={Colors.HIGH_EMPHASIS}
            >
              {t('purchase.cardTotal')}
            </Text>
            <Text
              type={Typography.GSBUTTON}
              color={Colors.HIGH_EMPHASIS}
            >
              {`$${total}`}
            </Text>
          </Flex>
          <Flex
            align="center"
            justify="space-between"
            style={serviceFeeflexContainerStyle}
          >
            <Text
              type={Typography.GSBODY3}
              color={Colors.HIGH_EMPHASIS}
            >
              {t('purchase.serviceFee')}
            </Text>
            <Text
              type={Typography.GSBUTTON}
              color={Colors.HIGH_EMPHASIS}
            >
              {`$${fee}`}
            </Text>
          </Flex>

          <Flex
            align="baseline"
            justify="space-between"
            style={rowPaddingTop}
          >
            <Text type={Typography.GSH6}>
              <Trans
                i18nKey={t('customizeCard.goalCard')}
                components={{
                  span: <span style={goalCardRegisteredTrademarkStyle} />,
                }}
              />
            </Text>
            <Text type={Typography.GSH4}>{`$${totalWithFee}`}</Text>
          </Flex>
        </Card>
      </Flex>
      {isMobile && (
        <div style={termsStyle(true)}>
          <Button
            type={TypesButton.PRIMARY}
            onClick={handleSubmit}
            style={continueButtonStyle}
            isLoading={loading}
            isDisabled={disableSubmitButton}
          >
            {t('purchase.submitPayment')}
          </Button>
          <Trans
            i18nKey={t('purchase.terms')}
            components={{
              span: (
                <Link
                  to="https://stripe.com/legal/end-users"
                  target="_blank"
                  style={termsLinkStyle}
                  data-testid="linkStripeTerms"
                />
              ),
            }}
          />
        </div>
      )}
    </Container>
  );
};
